import React, { useMemo } from 'react';
import { defaultFontFamilyOptions } from 'app/pages/design/landingPageBuilder/components';
import { FormikDropdownField } from 'lib/components/formik/FormikDropdown';
import { FormikNumberField } from 'lib/components/formik/FormikNumberField';
import { FormikSwitchField } from 'lib/components/formik/FormikSwitchField';
import { Flex, Gap } from 'lib/components/styles/layout';
import {
  FontSizes,
  FontWeights,
  FontCases,
  TEXT_ALIGNMENT,
  TEXT_ORIENTATION,
  LpBuilderFormMode,
  LP_BUILDER_FORM_MODE,
  GradientTypes,
  LandingPageBuilderLogos,
} from '../constants';
import { Button, useCovideoTheme } from 'react-covideo-common';
import { useFontFamilyOptions } from '../helpers/useFontFamilyOptions';
import { FormikCheckboxField } from 'lib/components/formik/FormikCheckbox';
import { FaAlignCenter, FaAlignLeft, FaAlignRight } from 'react-icons/fa';
import { FormikRadioField } from 'lib/components/formik/FormikRadioField';
import AdditionalTextEditor from '../components/AdditionalTextEditor';
import { FormikColorPicker } from 'lib/components/formik/FormikColorPicker';
import { SocialIcon } from '../helpers/socialIcons';
import { ILandingPageBuilderBulkEditFormData } from '../helpers/getInitialLandingPageValuesForBulkEdit';

export interface ChangesSummaryField {
  name: string;
  jsx: (value?: any) => JSX.Element;
}

export interface ChangesSummarySection {
  title: string;
  fields: ChangesSummaryField[];
}

export interface ChangesSummaryForm {
  form: string;
  title: string;
  sections: ChangesSummarySection[];
}

export const useChangesSummaryMapper = (
  values: ILandingPageBuilderBulkEditFormData,
  mode: LpBuilderFormMode
) => {
  const themes = useCovideoTheme();
  const isBulkEditMode = mode === LP_BUILDER_FORM_MODE.BULK_EDIT;
  const allFontFamilyOptions = useFontFamilyOptions(
    defaultFontFamilyOptions,
    isBulkEditMode
  );

  const commonLabelStyle = {
    labelStyles: {
      fontSize: '16px',
      marginLeft: '7px',
      fontWeight: 400,
      color: themes.colors.neutral[100],
    },
  };

  const commonSelectedStyle = {
    container: { border: `1px solid ${themes.colors.secondary[40]}` },
  };

  const commonFormikColorPickerStyle = {
    labelStyles: {
      fontSize: '12px',
      color: themes.colors.neutral[80],
      fontWeight: 600,
      marginBottom: '8px',
    },
  };

  const commonFormikSwitchFieldStyle = {
    labelStyles: {
      fontSize: '15px',
      lineHeight: '24px',
      fontWeight: 500,
      color: themes.colors.neutral[100],
    },
  };

  const fontCasesOptions = isBulkEditMode
    ? [{ label: 'None', value: null }, ...FontCases]
    : FontCases;

  return useMemo(
    () => [
      {
        form: 'image',
        title: 'Media Changes',
        sections: [
          {
            title: 'Background image',
            fields: [
              {
                name: 'bodyBackgroundImage',
                jsx: (value: any) =>
                  value?.url && (
                    <img
                      src={value.url}
                      alt='Logo'
                      style={{
                        height: '145px',
                        width: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  ),
              },
            ],
          },
          {
            title: 'Gradient overlay',
            fields: [
              {
                name: 'showGradient',
                jsx: () => (
                  <>
                    <FormikSwitchField
                      name='showGradient'
                      label='Gradient Overlay'
                      readOnly
                      extendStyles={commonFormikSwitchFieldStyle}
                      onChange={() => {}}
                    />
                    {!!values.showGradient && values.gradient ? (
                      <FormikDropdownField
                        name='gradient'
                        options={GradientTypes}
                        readonly
                      />
                    ) : (
                      <></>
                    )}
                  </>
                ),
              },
            ],
          },
        ],
      },
      {
        form: 'header',
        title: 'Header Changes',
        sections: [
          {
            title: 'Typography',
            fields: [
              {
                name: 'headerFontFamily',
                jsx: () => (
                  <FormikDropdownField
                    options={allFontFamilyOptions}
                    name='headerFontFamily'
                    label='Select a Font'
                    readonly
                    hideError
                  />
                ),
              },
              {
                name: 'titleFontSize',
                jsx: () => (
                  <FormikDropdownField
                    options={[{ label: 'None', value: null }, ...FontSizes]}
                    name='titleFontSize'
                    label='Font Size'
                    readonly
                    extendStyles={{
                      labelStyles: { fontSize: '14px' },
                    }}
                    hideError
                  />
                ),
              },
              {
                name: 'headerLineHeight',
                jsx: () => (
                  <FormikNumberField
                    label='Line height'
                    name='headerLineHeight'
                    suffix='px'
                    readOnly
                    min={0}
                    isRequired={false}
                    hideError
                  />
                ),
              },
              {
                name: 'headerFontWeight',
                jsx: () => (
                  <FormikDropdownField
                    options={FontWeights}
                    name='headerFontWeight'
                    label='Font Weight'
                    readonly
                    extendStyles={{
                      labelStyles: { fontSize: '14px' },
                    }}
                    hideError
                  />
                ),
              },
              {
                name: 'headerFontCase',
                jsx: () => (
                  <FormikDropdownField
                    name='headerFontCase'
                    options={[{ label: 'None', value: null }, ...FontCases]}
                    label='Case'
                    readonly
                    hideError
                  />
                ),
              },
            ],
          },
          {
            title: 'Color',
            fields: [
              {
                name: 'headerBackgroundColor',
                jsx: () => (
                  <FormikColorPicker
                    label='Header Background Color'
                    name='headerBackgroundColor'
                    extendStyles={commonFormikColorPickerStyle}
                    onInputChange={() => {}}
                    readonly
                  />
                ),
              },
              {
                name: 'headerTextColor',
                jsx: () => (
                  <FormikColorPicker
                    label='Text Color'
                    name='headerTextColor'
                    extendStyles={commonFormikColorPickerStyle}
                    onInputChange={() => {}}
                    readonly
                  />
                ),
              },
            ],
          },
          {
            title: 'Shadow',
            fields: [
              {
                name: 'showHeaderShadow',
                jsx: () => (
                  <FormikSwitchField
                    name='showHeaderShadow'
                    label='Show Shadow'
                    readOnly
                    extendStyles={commonFormikSwitchFieldStyle}
                    onChange={() => {}}
                  />
                ),
              },
            ],
          },
          {
            title: 'Logo',
            fields: [
              {
                name: 'headerLogo',
                jsx: (value: any) =>
                  value?.url && (
                    <img
                      src={value.url}
                      alt='Logo'
                      style={{
                        height: '145px',
                        width: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  ),
              },
              {
                name: 'headerLogoMaxWidth',
                jsx: () => (
                  <FormikNumberField
                    label='Max width'
                    name='headerLogoMaxWidth'
                    suffix='px'
                    min={0}
                    isRequired={false}
                    hideError
                    readOnly
                  />
                ),
              },
              {
                name: 'headerLogoMaxHeight',
                jsx: () => (
                  <FormikNumberField
                    label='Max height'
                    name='headerLogoMaxHeight'
                    suffix='px'
                    min={0}
                    isRequired={false}
                    hideError
                    readOnly
                  />
                ),
              },
            ],
          },
          {
            title: 'Contact Info',
            fields: [
              {
                name: 'showFirstName',
                jsx: () => (
                  <FormikCheckboxField
                    name={'showFirstName'}
                    label='First Name'
                    onChange={() => {}}
                    extendStyles={commonLabelStyle}
                    readOnly
                  />
                ),
              },
              {
                name: 'showLastName',
                jsx: () => (
                  <FormikCheckboxField
                    name={'showLastName'}
                    label='Last Name'
                    onChange={() => {}}
                    extendStyles={commonLabelStyle}
                    readOnly
                  />
                ),
              },
              {
                name: 'showPhone1',
                jsx: () => (
                  <FormikCheckboxField
                    name={'showPhone1'}
                    label='Phone 1'
                    onChange={() => {}}
                    extendStyles={commonLabelStyle}
                    readOnly
                  />
                ),
              },
              {
                name: 'showPhone2',
                jsx: () => (
                  <FormikCheckboxField
                    name={'showPhone2'}
                    label='Phone 2'
                    onChange={() => {}}
                    extendStyles={commonLabelStyle}
                    readOnly
                  />
                ),
              },
              {
                name: 'showDepartment',
                jsx: () => (
                  <FormikCheckboxField
                    name={'showDepartment'}
                    label='Department'
                    onChange={() => {}}
                    extendStyles={commonLabelStyle}
                    readOnly
                  />
                ),
              },
              {
                name: 'showTitle',
                jsx: () => (
                  <FormikCheckboxField
                    name={'showTitle'}
                    label='Title'
                    onChange={() => {}}
                    extendStyles={commonLabelStyle}
                    readOnly
                  />
                ),
              },
              {
                name: 'showWebsite',
                jsx: () => (
                  <FormikCheckboxField
                    name={'showWebsite'}
                    label='Website'
                    onChange={() => {}}
                    extendStyles={commonLabelStyle}
                    readOnly
                  />
                ),
              },
              {
                name: 'showEmail',
                jsx: () => (
                  <FormikCheckboxField
                    name={'showEmail'}
                    label='Email'
                    onChange={() => {}}
                    extendStyles={commonLabelStyle}
                    readOnly
                  />
                ),
              },
            ],
          },
          {
            title: 'Content Alignment',
            fields: [
              {
                name: 'headerContentAlignment',
                jsx: () => (
                  <Gap gap='20px' flexDirection='row'>
                    <Button
                      variant='secondary'
                      icon={<FaAlignLeft />}
                      extendStyles={
                        values.headerContentAlignment === TEXT_ALIGNMENT.LEFT
                          ? commonSelectedStyle
                          : {}
                      }
                      onClick={() => {}}
                    />
                    <Button
                      variant='secondary'
                      icon={<FaAlignCenter />}
                      extendStyles={
                        values.headerContentAlignment === TEXT_ALIGNMENT.CENTER
                          ? commonSelectedStyle
                          : {}
                      }
                      onClick={() => {}}
                    />
                    <Button
                      variant='secondary'
                      icon={<FaAlignRight />}
                      extendStyles={
                        values.headerContentAlignment === TEXT_ALIGNMENT.RIGHT
                          ? commonSelectedStyle
                          : {}
                      }
                      onClick={() => {}}
                    />
                  </Gap>
                ),
              },
            ],
          },
          {
            title: 'Content orientation',
            fields: [
              {
                name: 'headerContentOrientation',
                jsx: () => (
                  <Gap
                    flexDirection='column'
                    alignItems='flex-start'
                    gap='16px'
                  >
                    <Flex gap='16px'>
                      <FormikRadioField
                        name='headerContentOrientation'
                        label='Vertical'
                        value={TEXT_ORIENTATION.VERTICAL}
                        extendStyles={{ labelWrapper: { marginLeft: '36px' } }}
                        readonly
                      />
                      <FormikRadioField
                        name='headerContentOrientation'
                        value={TEXT_ORIENTATION.HORIZONTAL}
                        label='Horizontal'
                        extendStyles={{ labelWrapper: { marginLeft: '36px' } }}
                        readonly
                      />
                    </Flex>
                  </Gap>
                ),
              },
            ],
          },
          {
            title: 'showContactIcons',
            fields: [
              {
                name: '',
                jsx: () => (
                  <Button
                    variant='secondary'
                    icon={<FaAlignRight />}
                    extendStyles={
                      values.headerContentAlignment === TEXT_ALIGNMENT.RIGHT
                        ? commonSelectedStyle
                        : {}
                    }
                    onClick={() => {}}
                  />
                ),
              },
            ],
          },
        ],
      },
      {
        form: 'body',
        title: 'Body Changes',
        sections: [
          {
            title: 'Color',
            fields: [
              {
                name: 'bodyBackgroundColor',
                jsx: () => (
                  <FormikColorPicker
                    label='Background Color'
                    name='bodyBackgroundColor'
                    extendStyles={commonFormikColorPickerStyle}
                    readonly
                    onInputChange={() => {}}
                  />
                ),
              },
            ],
          },
          {
            title: 'Slogan',
            fields: [
              {
                name: 'additionalText',
                jsx: () => (
                  <AdditionalTextEditor
                    initialContent={values.additionalText ?? undefined}
                    onTextEditorChange={() => {}}
                    readonly
                  />
                ),
              },
              {
                name: 'additionalTextFontFamily',
                jsx: () => (
                  <FormikDropdownField
                    options={allFontFamilyOptions}
                    name='additionalTextFontFamily'
                    label='Select a Font'
                    readonly
                    hideError
                  />
                ),
              },
              {
                name: 'additionalTextColor',
                jsx: () => (
                  <FormikColorPicker
                    label='Text Color'
                    name='additionalTextColor'
                    readonly
                    extendStyles={commonFormikColorPickerStyle}
                  />
                ),
              },
            ],
          },
        ],
      },
      {
        form: 'footer',
        title: 'Footer Changes',
        sections: [
          {
            title: 'Color',
            fields: [
              {
                name: 'footerBackgroundColor',
                jsx: () => (
                  <FormikColorPicker
                    label='Background Color'
                    name='footerBackgroundColor'
                    extendStyles={commonFormikColorPickerStyle}
                    readonly
                    onInputChange={() => {}}
                  />
                ),
              },
            ],
          },
          {
            title: 'Shadow',
            fields: [
              {
                name: 'showFooterShadow',
                jsx: () => (
                  <FormikSwitchField
                    name='showFooterShadow'
                    label='Show Shadow'
                    extendStyles={commonFormikSwitchFieldStyle}
                    onChange={() => {}}
                    readOnly
                  />
                ),
              },
            ],
          },
          {
            title: 'Logo',
            fields: [
              {
                name: 'footerLogo',
                jsx: () => (
                  <FormikDropdownField
                    options={[
                      { label: 'None', value: null },
                      ...LandingPageBuilderLogos,
                    ]}
                    name='footerLogo'
                    readonly
                    hideError
                  />
                ),
              },
            ],
          },
        ],
      },

      {
        form: 'ctas',
        title: 'CTAs Changes',
        sections: [
          {
            title: 'Typography',
            fields: [
              {
                name: 'buttonFontFamily',
                jsx: () => (
                  <FormikDropdownField
                    options={allFontFamilyOptions}
                    name={'buttonFontFamily'}
                    label='Select a Font'
                    readonly
                    hideError
                  />
                ),
              },
              {
                name: 'buttonFontSize',
                jsx: () => (
                  <FormikDropdownField
                    options={[{ label: 'None', value: null }, ...FontSizes]}
                    name={'buttonFontSize'}
                    label='Font Size'
                    extendStyles={{
                      labelStyles: { fontSize: '14px' },
                    }}
                    hideError
                    readonly
                  />
                ),
              },
              {
                name: 'buttonFontWeight',
                jsx: () => (
                  <FormikDropdownField
                    options={FontWeights}
                    name='buttonFontWeight'
                    label='Font Weight'
                    readonly
                    extendStyles={{
                      labelStyles: { fontSize: '14px' },
                    }}
                    hideError
                  />
                ),
              },
              {
                name: 'buttonFontCase',
                jsx: () => (
                  <FormikDropdownField
                    options={fontCasesOptions}
                    name='buttonFontCase'
                    label='Font Case'
                    readonly
                    extendStyles={{
                      labelStyles: { fontSize: '14px' },
                    }}
                    hideError
                  />
                ),
              },
            ],
          },
          {
            title: 'Shape',
            fields: [
              {
                name: 'buttonCornerRadius',
                jsx: () => (
                  <FormikNumberField
                    label='Button Corner Radius'
                    name='buttonCornerRadius'
                    suffix='px'
                    min={0}
                    isRequired={false}
                    hideError
                    readOnly
                  />
                ),
              },
            ],
          },
          {
            title: 'Button color',
            fields: [
              {
                name: 'buttonColor',
                jsx: () => (
                  <FormikColorPicker
                    label='Button Color'
                    name='buttonColor'
                    extendStyles={commonFormikColorPickerStyle}
                    readonly
                  />
                ),
              },
              {
                name: 'buttonHoverColor',
                jsx: () => (
                  <FormikColorPicker
                    label='Button Color Hover'
                    name='buttonHoverColor'
                    extendStyles={commonFormikColorPickerStyle}
                    readonly
                  />
                ),
              },
            ],
          },
          {
            title: 'Button Border Color',
            fields: [
              {
                name: 'showButtonBorder',
                jsx: () => (
                  <FormikSwitchField
                    name='showButtonBorder'
                    label='Button Color'
                    readOnly
                    extendStyles={commonFormikSwitchFieldStyle}
                    onChange={() => {}}
                  />
                ),
              },
              {
                name: 'buttonBorderColor',
                jsx: () => (
                  <FormikColorPicker
                    label='Button Border Color'
                    name='buttonBorderColor'
                    extendStyles={commonFormikColorPickerStyle}
                    readonly
                  />
                ),
              },
              {
                name: 'buttonBorderHoverColor',
                jsx: () => (
                  <FormikColorPicker
                    label='Button Border Hover Color'
                    name='buttonBorderHoverColor'
                    extendStyles={commonFormikColorPickerStyle}
                    readonly
                  />
                ),
              },
            ],
          },
          {
            title: 'Button Text Color',
            fields: [
              {
                name: 'buttonTextColor',
                jsx: () => (
                  <FormikColorPicker
                    label='Button Text Color'
                    name='buttonTextColor'
                    extendStyles={commonFormikColorPickerStyle}
                    readonly
                  />
                ),
              },
              {
                name: 'buttonTextHoverColor',
                jsx: () => (
                  <FormikColorPicker
                    label='Button Text Hover Color'
                    name='buttonTextHoverColor'
                    extendStyles={commonFormikColorPickerStyle}
                    readonly
                  />
                ),
              },
            ],
          },
          {
            title: 'Button Padding',
            fields: [
              {
                name: 'buttonPaddingY',
                jsx: () => (
                  <FormikNumberField
                    label='Top/Bottom'
                    name='buttonPaddingY'
                    suffix='px'
                    min={0}
                    isRequired={false}
                    hideError
                    readOnly
                  />
                ),
              },
              {
                name: 'buttonPaddingX',
                jsx: () => (
                  <FormikNumberField
                    label='Left/Right'
                    name='buttonPaddingX'
                    suffix='px'
                    min={0}
                    isRequired={false}
                    hideError
                    readOnly
                  />
                ),
              },
            ],
          },
        ],
      },
      {
        form: 'socials',
        title: 'Social Media Changes',
        sections: [
          {
            title: 'Icons',
            fields: [
              {
                name: 'showSocialMediaIcons',
                jsx: () => (
                  <FormikSwitchField
                    extendStyles={{
                      wrapper: { margin: '0 0 16px 0' },
                      labelStyles: {
                        fontSize: '15px',
                        lineHeight: '24px',
                        fontWeight: 500,
                        color: themes.colors.neutral[100],
                      },
                    }}
                    name='showSocialMediaIcons'
                    label='Show Icons'
                    onChange={() => {}}
                    readOnly
                  />
                ),
              },
            ],
          },
          {
            title: 'Social Media Icon Design',
            fields: [
              {
                name: 'socialMediaIconType',
                jsx: () =>
                  values.showSocialMediaIcons && values.socialMediaIconType ? (
                    <SocialIcon
                      icon='facebook'
                      design={values.socialMediaIconType}
                      svgSize={24}
                      useOutlinedIcon
                    />
                  ) : (
                    <></>
                  ),
              },
            ],
          },
        ],
      },
    ],
    [allFontFamilyOptions]
  );
};
